import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { patchState } from '@ngrx/signals';
import { WebSocketService } from './web-socket.service';
import { AuthService } from './auth.service';
import { TaskStore } from '@stores/task.store';
import { Task } from '@interfaces/task/task';
import { TopicType } from '../enums/topic-type';
import { TaskAssignmentRequest } from '@features/dashboard/interfaces/task-assignment-request';
import { TaskStatus } from '../enums/task-status';
import { CloseTaskRequest } from '@interfaces/task/close-task-request';
import { AssistReqNotification } from '@interfaces/request-assist/assist-req-notification';
import { CloseAssistReqRequest } from '@interfaces/request-assist/close-assistreq-request';
import { PageInfo } from '@interfaces/page-info/page-info';

type DrawerDataKind = 'task' | 'assistreq' | 'nurse' | 'alarm';
type TaskUpdatePayload =
  | (Partial<Task> & {
      accepted?: boolean;
      taskId?: string;
      notificationId?: string;
      data?: Partial<Task['data']> & {
        sequence?: string;
        status?: TaskStatus;
        isWctpAccepted?: boolean;
      };
    })
  | any;

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  drawerPending = signal<DrawerDataKind[]>([]);

  taskPageInfo = computed(() => this.taskStore.taskPageInfo());
  assistreqPageInfo = computed(() => this.taskStore.assistreqPageInfo());
  nursePageInfo = computed(() => this.taskStore.nursePageInfo());
  alarmPageInfo = computed(() => this.taskStore.alarmPageInfo());
  private webSocketService = inject(WebSocketService);
  private authService = inject(AuthService);
  private taskStore = inject(TaskStore);
  tasks = this.taskStore.tasks as WritableSignal<Task[]>;

  startDrawerLoading() {
    this.drawerPending.set(['task', 'assistreq', 'nurse', 'alarm']);
  }

  finishDrawerLoading(kind: DrawerDataKind) {
    const pending = this.drawerPending();
    if (!pending.includes(kind)) {
      return;
    }
    this.drawerPending.set(pending.filter((k) => k !== kind));
  }

  readTasks() {
    const readedTasks = this.tasks().map((task) => ({ ...task, isRead: true }));
    this.taskStore.updateTasks(readedTasks);
  }
  addTasks(tasks: Task[], pageInfo: PageInfo, isTask: boolean = true) {
    tasks = tasks.map((task) => ({
      ...task,
      isTask,
    }));

    const existingTasks = this.tasks();
    const incomingBySequence = new Map(
      tasks.map((task) => [task.sequence, task] as const),
    );

    const mergedExistingTasks = existingTasks.map((existingTask) => {
      const incomingTask = incomingBySequence.get(existingTask.sequence);
      if (!incomingTask) {
        return existingTask;
      }

      const mergedTask = {
        ...existingTask,
        ...incomingTask,
        data: {
          ...existingTask.data,
          ...incomingTask.data,
        },
      };

      if (existingTask.data?.patient && !incomingTask.data?.patient) {
        mergedTask.data.patient = existingTask.data.patient;
      }

      incomingBySequence.delete(existingTask.sequence);
      return mergedTask;
    });

    patchState(this.taskStore as any, {
      tasks: [...mergedExistingTasks, ...incomingBySequence.values()],
      taskPageInfo: pageInfo,
    });
  }

  setTasks(tasks: Task[], pageInfo: PageInfo) {
    this.taskStore.setTasks(tasks, pageInfo);
  }

  updateAssistRequestTask(res: AssistReqNotification) {
    const task: Task = {
      sequence: res.sequence,
      key: res.key,
      type: res.type,
      status: res.status,
      data: {
        title: res.data.title,
        description: res.data.description,
        location: res.data.location,
        startTime: res.data.startTime,
        requester: res.data.requester,
      },
      activeResponder: {
        id: res?.activeResponder?.id,
        name: res?.activeResponder?.name ?? '',
      },
      isRead: false,
      isHighlighted: false,
    };
    this.updateTask(task);
  }

  sendTaskAssignmentResponse(assignedUserId: string, sequenceId: string) {
    const userId = this.authService.user().id;
    const message: TaskAssignmentRequest = {
      sequence: sequenceId,
      responder: userId!,
      assignedUser: assignedUserId,
    };

    this.webSocketService.send({
      topic: TopicType.TASK_ASSIGNMENT_RESPONSE,
      message,
      regenerateUuid: true,
    });
  }

  updateTaskStatus(task: TaskUpdatePayload) {
    const normalizedTask = this.normalizeTaskUpdate(task);

    const mappedTasks = this.tasks().map((t) => {
      if (t.sequence === normalizedTask.sequence) {
        const mergedTask = { ...t, ...normalizedTask };
        if (typeof normalizedTask.isWctpAccepted === 'boolean') {
          mergedTask.isWctpAccepted = normalizedTask.isWctpAccepted;
        }
        if (typeof normalizedTask.accepted === 'boolean') {
          mergedTask.isWctpAccepted = normalizedTask.accepted;
        }
        return mergedTask;
      }
      return t;
    });
    this.taskStore.updateTasks(mappedTasks);
  }

  updateTask(task: TaskUpdatePayload, isTask: boolean = true) {
    const normalizedTask = this.normalizeTaskUpdate(task);

    const mappedTasks = this.tasks().map((t) => {
      if (t.sequence === normalizedTask.sequence) {
        const mergedTask = { ...t, ...normalizedTask };
        if (typeof normalizedTask.isWctpAccepted === 'boolean') {
          mergedTask.isWctpAccepted = normalizedTask.isWctpAccepted;
        }
        if (typeof normalizedTask.accepted === 'boolean') {
          mergedTask.isWctpAccepted = normalizedTask.accepted;
        }
        if (t.data?.patient) {
          mergedTask.data.patient = t.data.patient;
        }
        if (TaskStatus.INITIATED === mergedTask.status) {
          mergedTask.status = TaskStatus.ACTIVE;
        }
        return mergedTask;
      }
      return t;
    });
    this.taskStore.updateTasks(mappedTasks);
  }

  private normalizeTaskUpdate(task: TaskUpdatePayload): TaskUpdatePayload {
    const payload = task?.message?.data ?? task?.data?.nurseCall ?? task?.data ?? task;

    const sequence =
      payload?.sequence ??
      payload?.data?.sequence ??
      payload?.notificationId ??
      payload?.taskId?.replace(/^@/, '') ??
      task?.sequence ??
      task?.notificationId ??
      task?.taskId?.replace(/^@/, '');

    const status =
      payload?.status ??
      payload?.data?.status ??
      task?.status ??
      task?.data?.status;
    const isWctpAccepted =
      payload?.isWctpAccepted ??
      payload?.data?.isWctpAccepted ??
      task?.isWctpAccepted ??
      task?.data?.isWctpAccepted ??
      undefined;

    return {
      ...task,
      ...payload,
      sequence,
      status,
      isWctpAccepted,
    };
  }

  addTask(assignment: Task, status = TaskStatus.INITIATED, isTask = true) {
    this.taskStore.addTask(assignment, status);
  }

  addAssistRequestTask(
    assistRequest: AssistReqNotification,
    status = TaskStatus.INITIATED,
  ) {
    const task: Task = {
      sequence: assistRequest.sequence,
      key: assistRequest.key,
      type: assistRequest.type,
      status: assistRequest.status,
      data: {
        title: assistRequest.data.title,
        description: assistRequest.data.description,
        location: assistRequest.data.location,
        startTime: assistRequest.data.startTime,
        requester: assistRequest.data.requester,
      },
      isRead: false,
      isHighlighted: false,
      departments: assistRequest.data.departments,
    };

    this.taskStore.addTask(task, status);
  }

  addAssistRequestTasks(
    assistreqs: AssistReqNotification[],
    pageInfo: PageInfo,
  ) {
    const tasks = assistreqs.map((assistreq: AssistReqNotification) => ({
      sequence: assistreq.sequence,
      key: assistreq.key,
      type: assistreq.type,
      status: assistreq.status,
      data: {
        title: assistreq.data.title,
        description: assistreq.data.description,
        location: assistreq.data.location,
        startTime: assistreq.data.startTime,
        requester: assistreq.data.requester,
      },
      activeResponder: {
        id: assistreq.activeResponder?.id,
        name: assistreq.activeResponder?.name ?? '',
      },
      isRead: false,
      isHighlighted: false,
      departments: assistreq.data.departments,
    }));
    this.taskStore.addAssistreqTasks(tasks, pageInfo);
  }

  isTaskExists(locationName: string): boolean {
    return this.tasks().some((t) => {
      const locName =
        typeof t.data?.location === 'string'
          ? t.data?.location
          : t.data?.location?.name;
      return locName === locationName;
    });
  }

  toggleHighlightTasksByLocationName(locationName?: string) {
    const tasks = this.tasks().reduce((acc: Task[], elem: Task) => {
      const locName =
        typeof elem.data?.location === 'string'
          ? elem.data?.location
          : elem.data?.location?.name;
      if (locName === locationName) {
        return [{ ...elem, isHighlighted: !elem.isHighlighted }, ...acc];
      }
      return [...acc, { ...elem, isHighlighted: false }];
    }, []);
    this.taskStore.updateTasks(tasks);
  }

  closeTask(task: Task) {
    const userId = this.authService.user().id;

    const types = ['cleaning', 'portering', 'staffAssistance'];
    if (types.includes(task.type)) {
      /* Close assist request request */
      const message: CloseAssistReqRequest = {
        key: task.key,
        requester: userId!.replace('@', ''),
        notificationId: task.sequence,
      };
      this.webSocketService.send({
        topic: TopicType.CLOSE_ASSISTREQ_REQUEST,
        message,
      });
    } else {
      /* Close task request */
      const message: CloseTaskRequest = {
        key: task.key,
        requester: userId!,
        taskId: `@${task.sequence}`,
        type: task.type,
      };
      this.webSocketService.send({
        topic: TopicType.CLOSE_TASK_REQUEST,
        message,
      });
    }
  }
}
